/* eg-recherche-recettes.js : recettes dans la recherche du site (16/10/2026, audit par inversion : « trouver par le plat »)
   et dans le panier (« avec ce panier, cuisinez… » : recettes dont le client a déjà des produits, et ce qu'il manque).
   Module chargé par sections/eg-search.liquid quand une recherche est faite. Lit l'index des recettes publiées
   (/pages/recettes?view=index-recettes&page=N, section eg-recettes-json, 250 par page), le garde 10 minutes dans
   sessionStorage, et montre au plus 8 recettes dont le titre ou les ingrédients contiennent les mots cherchés.
   Mots et correspondance : mêmes règles que eg-rcia-arbre.js (normaliser, mots, touche ; parité testée sous Node).
   Textes posés par textContent ; adresses limitées aux chemins du site et aux images du CDN. */

const VIDES = new Set(('avec sans pour une des les aux pas que qui est son ses mon mes vos votre nos notre leur plat plats '
  + 'recette recettes envie envies faire fait idee idees chose quelque bien tres plus peu moins comme dans sur par chez '
  + 'aime aimerais voudrais voulez veux bon bonne bons bonnes petit petite grand grande vraie vrai style facon maison').split(' '));
export const CLE = 'eg-rc-index';
export const DUREE_CACHE = 10 * 60 * 1000;
export const MAX = 8;
const RE_U = /^\/(?!\/)[A-Za-z0-9/._~%-]*$/;
const RE_I = /^(https:)?\/\/[^\s"'<>\\]+$/;

export function normaliser(t) {
  return String(t == null ? '' : t).toLowerCase().replace(/œ/g, 'oe').replace(/æ/g, 'ae')
    .normalize('NFD').replace(/[̀-ͯ]/g, '');
}
function uniques(v) { return Array.isArray(v) ? [...new Set(v.filter((x) => typeof x === 'string'))] : []; }
export function mots(t) {
  return uniques(normaliser(t).split(/[^a-z0-9]+/).filter((m) => m.length >= 3 && !VIDES.has(m))
    .map((m) => (m.length > 4 ? m.replace(/[sx]$/, '') : m))).slice(0, 12);
}
export function touche(demandes, presents) {
  if (!demandes || !demandes.length || !presents || !presents.length) return 0;
  return demandes.filter((m) => presents.some((w) => w === m
    || (Math.min(w.length, m.length) >= 5 && (w.startsWith(m) || m.startsWith(w))))).length;
}

/** Entrées sûres de l'index (adresse du site, titre non vide), avec leurs mots (titre à part pour le classement). */
export function entrees(liste) {
  const vus = new Set();
  const out = [];
  for (const r of Array.isArray(liste) ? liste : []) {
    if (!r || typeof r.u !== 'string' || !RE_U.test(r.u) || typeof r.t !== 'string' || !r.t.trim() || vus.has(r.u)) continue;
    vus.add(r.u);
    out.push({ u: r.u, t: r.t, m: Number.isInteger(r.m) && r.m > 0 ? r.m : 0, nv: typeof r.nv === 'string' ? r.nv : '',
      i: typeof r.i === 'string' && RE_I.test(r.i) ? r.i : '', mt: mots(r.t), mw: mots(r.t + ' ' + (typeof r.w === 'string' ? r.w.slice(0, 600) : '')),
      p: Array.isArray(r.p) ? [...new Set(r.p.filter((x) => Number.isInteger(x) && x > 0))] : [] });
  }
  return out;
}

/** Recettes pour une recherche : tous les mots trouvés d'abord, le titre avant les ingrédients, puis l'ordre alphabétique.
    Une recherche de plusieurs mots exige au moins la moitié d'entre eux (« sauce tomate basilic » ne remonte pas toutes
    les recettes au basilic). */
export function chercher(index, q, max = MAX) {
  const d = mots(q);
  if (!d.length) return [];
  const seuil = Math.max(1, Math.ceil(d.length / 2));
  return index
    .map((y) => ({ y, n: touche(d, y.mw), nt: touche(d, y.mt) }))
    .filter((x) => x.n >= seuil)
    .sort((a, b) => b.n - a.n || b.nt - a.nt || a.y.t.localeCompare(b.y.t, 'fr'))
    .slice(0, max)
    .map((x) => x.y);
}

/** Recettes pour un panier : au moins un produit en commun ; d'abord la part des produits déjà là, puis leur nombre,
    puis l'ordre alphabétique. Renvoie {y, ont, manque}. */
export function suggerer(index, pidsPanier, max = 3) {
  const panier = new Set((pidsPanier || []).map(Number).filter((x) => Number.isInteger(x) && x > 0));
  if (!panier.size) return [];
  return index
    .filter((y) => y.p.length)
    .map((y) => { const ont = y.p.filter((x) => panier.has(x)).length; return { y, ont, manque: y.p.length - ont }; })
    .filter((x) => x.ont > 0)
    .sort((a, b) => (b.ont / b.y.p.length) - (a.ont / a.y.p.length) || b.ont - a.ont || a.y.t.localeCompare(b.y.t, 'fr'))
    .slice(0, max);
}

function lireCache() {
  try {
    const o = JSON.parse(sessionStorage.getItem(CLE) || 'null');
    if (o && Array.isArray(o.r) && Date.now() - o.t < DUREE_CACHE) return o.r;
  } catch (err) { /* stockage indisponible */ }
  return null;
}

async function charger(src) {
  const cache = lireCache();
  if (cache) return cache;
  const page = async (p) => {
    const r = await fetch(src + (src.includes('?') ? '&' : '?') + 'page=' + p, { credentials: 'same-origin' });
    if (!r.ok) return null;
    const s = new DOMParser().parseFromString(await r.text(), 'text/html').querySelector('script[data-eg-rc-index]');
    try { return s ? JSON.parse(s.textContent) : null; } catch (err) { return null; }
  };
  const p1 = await page(1);
  if (!p1) return [];
  let r = Array.isArray(p1.r) ? p1.r : [];
  const n = Math.min(8, Number(p1.pages) || 1);
  if (n > 1) {
    const suite = await Promise.all(Array.from({ length: n - 1 }, (x, k) => page(k + 2)));
    suite.forEach((x) => { if (x && Array.isArray(x.r)) r = r.concat(x.r); });
  }
  try { sessionStorage.setItem(CLE, JSON.stringify({ t: Date.now(), r })); } catch (err) { /* stockage plein ou bloqué */ }
  return r;
}

function carte(y, T, repli) {
  const a = document.createElement('a');
  a.className = 'eg-prc';
  a.href = y.u;
  const fig = document.createElement('span');
  fig.className = 'eg-prc-i';
  const img = document.createElement('img');
  img.alt = '';
  img.width = 240;
  img.height = 180;
  img.loading = 'lazy';
  if (y.i) img.src = y.i;
  else { img.src = repli; img.className = 'eg-prc-ill'; }
  fig.appendChild(img);
  const b = document.createElement('b');
  b.textContent = y.t;
  if (T.lang) b.lang = T.lang;
  a.append(fig, b);
  const meta = [y.m ? String(T.minutes || '__N__ min').replace('__N__', y.m) : '', y.nv].filter(Boolean).join(' · ');
  if (meta) { const s = document.createElement('small'); s.textContent = meta; a.appendChild(s); }
  return a;
}

async function init(root) {
  if (root.dataset.egPret) return;
  root.dataset.egPret = '1';
  const q = root.dataset.q || '';
  const src = root.dataset.src || '';
  if (!q.trim() || !RE_U.test(src.split('?')[0]) || typeof fetch !== 'function' || typeof DOMParser !== 'function') return;
  const T = { minutes: root.dataset.lMinutes, lang: root.dataset.lang || '' };
  try {
    const liste = chercher(entrees(await charger(src)), q);
    if (!liste.length) return;
    const rail = root.querySelector('[data-eg-rc-rail]');
    liste.forEach((y) => rail.appendChild(carte(y, T, root.dataset.repli || '')));
    root.hidden = false; // flèches sur ordinateur : posées au chargement par eg-header, recalculées à l'affichage
  } catch (err) { /* index indisponible : la recherche de produits reste entière */ }
}

/* Panier : relu au chargement et à chaque changement (« eg:cart » d'eg-app.js). */
function initPanier(root) {
  if (root.dataset.egPret) return;
  root.dataset.egPret = '1';
  const src = root.dataset.src || '';
  if (!RE_U.test(src.split('?')[0]) || typeof fetch !== 'function' || typeof DOMParser !== 'function') return;
  const T = { minutes: root.dataset.lMinutes, lang: root.dataset.lang || '' };
  const rail = root.querySelector('[data-eg-rc-rail]');
  const racine = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let index = null;
  let tour = 0;
  async function peindre() {
    const n = ++tour;
    try {
      const [idx, panier] = await Promise.all([
        index ? Promise.resolve(index) : charger(src).then((r) => (index = entrees(r))),
        fetch(racine + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()),
      ]);
      if (n !== tour) return;
      const liste = suggerer(idx, ((panier && panier.items) || []).map((it) => it.product_id));
      rail.textContent = '';
      liste.forEach((x) => {
        const a = carte(x.y, T, root.dataset.repli || '');
        const s = document.createElement('small');
        s.className = 'eg-prc-m';
        s.textContent = x.manque ? String(x.manque === 1 ? root.dataset.lManqueUn : root.dataset.lManque).replace('__N__', x.manque) : root.dataset.lComplet;
        a.appendChild(s);
        rail.appendChild(a);
      });
      root.hidden = !liste.length;
    } catch (err) { /* panier ou index indisponible : rien à proposer */ }
  }
  peindre();
  document.addEventListener('eg:cart', peindre);
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-eg-rc-recherche]').forEach(init);
  document.querySelectorAll('[data-eg-rc-panier]').forEach(initPanier);
}
